import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

/**
 * Odliczanie do terminu składania ofert — pigułka z segmentami, świadomie „na FOMO".
 *
 * Tyka co sekundę pod dobę (liczy się każda minuta), wyżej co minutę. Ostatnia doba
 * robi się czerwona i pulsuje; po terminie pokazuje neutralne „Po terminie".
 */

function parts(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return {
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    s: s % 60,
  };
}

function Seg({ value, unit, urgent }: { value: number; unit: string; urgent: boolean }) {
  return (
    <span className="flex flex-col items-center leading-none">
      <span
        className={`text-lg font-bold tabular-nums ${
          urgent ? "text-red-600 dark:text-red-400" : "text-[var(--color-text-primary)]"
        }`}
      >
        {String(value).padStart(2, "0")}
      </span>
      <span className="mt-0.5 font-mono text-[9px] uppercase tracking-wide text-[var(--color-text-muted)]">
        {unit}
      </span>
    </span>
  );
}

export function Countdown({ deadline }: { deadline: string }) {
  const target = new Date(deadline).getTime();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    // Tyka co sekundę zawsze — sekundy mają być widoczne i żywe niezależnie od tego,
    // ile zostało. `now` poza zależnościami: interwał ustawiamy raz na montowanie.
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);

  const left = target - now;

  if (left <= 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-1 text-xs font-medium text-[var(--color-text-muted)]">
        <Clock size={13} /> Po terminie
      </span>
    );
  }

  const { d, h, m, s } = parts(left);
  const urgent = left < 86_400_000;

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${
        urgent
          ? "animate-pulse border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40"
          : "border-[var(--color-border)] bg-[var(--color-bg-elevated)]"
      }`}
      title={`Do terminu: ${new Date(deadline).toLocaleString("pl")}`}
    >
      <Clock
        size={15}
        className={urgent ? "text-red-600 dark:text-red-400" : "text-[var(--color-text-muted)]"}
      />
      <span className="flex items-center gap-2">
        {d > 0 && (
          <>
            <Seg value={d} unit="dni" urgent={urgent} />
            <Sep />
          </>
        )}
        <Seg value={h} unit="godz" urgent={urgent} />
        <Sep />
        <Seg value={m} unit="min" urgent={urgent} />
        <Sep />
        <Seg value={s} unit="sek" urgent={urgent} />
      </span>
    </span>
  );
}

function Sep() {
  return <span className="pb-3 text-[var(--color-text-muted)]">:</span>;
}
